<template>
  <div class="guide">
    <fullscreen-modal :show="show" @close="close">
      <wizard
        class="wizard-steps"
        v-if="active === 'mutliLength'"
        :steps="steps"
        :activeStep="activeStep"
        :showLabels="showLabels"
        :disableStepClick="active === 'multiLength'"
        @stepClick="stepClick"
      />
      <how-can-i-help
        v-if="active === 'howCanIHelp'"
        @next="next"
        @prev="prev"
        @close="close"
        @multiLengthGuide="activateMultiLength"
        @tunableWhiteGuide="onTunableWhiteGuide"
        @oldProductsGuide="onOldProductsGuide"
      ></how-can-i-help>

      <start-your-selection
        v-if="active === 'guideMeThrough'"
        :tagFamilies="tagFamilies"
        @next="next"
        @prev="prev"
        @to="to"
      ></start-your-selection>
      <div
        class="dynamic-guide"
        v-for="tagFamily in tagFamilies"
        :key="tagFamily.tag"
      >
        <guide-component
          v-if="active === tagFamily.tag"
          :tagFamily="tagFamily"
          @prev="to('guideMeThrough')"
          @setAndClose="setAndClose"
        ></guide-component>
      </div>
      <select-family
        v-if="active === 'selectFamily'"
        ref="selectFamily"
        class="select-family"
        @prev="prev"
        @close="close"
        @to="to"
        @changeNextButtonVisibility="nextButtonVisible = $event"
        @changeNextButtonDisabled="nextButtonDisabled = $event"
        @changeShowResultsButtonVisibility="showResultsButtonVisible = $event"
        @changeShowResultsButtonDisabled="showResultsButtonDisabled = $event"
      ></select-family>

      <multi-length
        v-if="active === 'multiLength' && canI('MULTI_LENGTH')"
        :activeStep="activeStep"
        :steps="steps"
        :withModule="withModule"
        @setActiveStep="setActiveStep"
        @next="next"
        @prev="prev"
        @to="to"
        @close="close"
      ></multi-length>

      <template v-slot:stickyfootercontent v-if="active === 'selectFamily'">
        <div class="footer-content">
          <div class="footer-btns-container row">
            <div class="col">
              <div class="btn btn-outline-secondary" @click="onPrevFamilyStep">
                Previous
              </div>
            </div>

            <div class="col">
              <div
                class="btn btn-secondary continue-btn"
                :disabled="nextButtonDisabled"
                v-if="nextButtonVisible"
                :class="{ disabled: nextButtonDisabled }"
                @click="onNextFamilyStep"
              >
                Next
              </div>
            </div>
          </div>

          <div class="show-results-wrapper">
            <a
              v-if="showResultsButtonVisible"
              class="link-btn"
              href="javascript:void(0)"
              :disabled="showResultsButtonDisabled"
              @click="onShowFamilyResults"
            >
              Show results
            </a>
          </div>
        </div>
      </template>

      <video-tutorials
        v-if="active === 'guideShowVideos'"
        @prev="prev"
      ></video-tutorials>
    </fullscreen-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { EventBus } from "@/eventbus.js";
import HowCanIHelp from "@/views/guide/steps/HowCanIHelp";
import VideoTutorials from "@/views/guide/steps/VideoTutorials";
import SelectFamily from "@/views/guide/steps/SelectFamily";
import StartYourSelection from "@/views/guide/steps/StartYourSelection";
import MultiLength from "@/views/guide/steps/MultiLength";
import GuideComponent from "@/components/guide/GuideComponent";

export default {
  name: "GuideView",
  components: {
    StartYourSelection,
    SelectFamily,
    HowCanIHelp,
    VideoTutorials,
    GuideComponent,
    MultiLength
  },
  props: {
    multiLength: {
      type: Boolean,
      default: false
    },
    withModule: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeStep: null,
      show: true,
      defaultOrderMap: [
        { name: "howCanIHelp" },
        { name: "guideMeThrough" },
        { name: "guideShowVideos" },
        // <-- Dynamic tag guides will be added here -->
        { name: "selectFamily", step: "Select family" }
      ],
      multiLengthOrderMap: [
        { name: "multiLength", step: "Select family" },
        { name: "multiLength", step: "Select filter" },
        { name: "multiLength", step: "Multi length result" }
      ],
      showLabels: true,
      active: "howCanIHelp",
      tagFamilies: [],
      nextButtonDisabled: true,
      nextButtonVisible: false,
      showResultsButtonDisabled: false,
      showResultsButtonVisible: false
    };
  },
  computed: {
    ...mapState("brain", ["region"]),
    orderMap() {
      if (this.active === "multiLength") {
        return this.multiLengthOrderMap;
      }
      return this.defaultOrderMap;
    },
    steps() {
      if (this.active === "multiLength") {
        return this.multiLengthOrderMap.map(item => item.step);
      }
      return this.orderMap
        .filter(item => item.step != null)
        .map(item => item.step);
    }
  },
  mounted() {
    this.loadTagFamiliesInOrderMap();

    if (this.multiLength) {
      this.activateMultiLength();
    }

    this.$store.dispatch("brain/global/getInitialTagFamilies").then(filters => {
      this.tagFamilies = filters.filter(
        tagFamily => tagFamily.availableValues.length
      );
    });
  },
  methods: {
    loadTagFamiliesInOrderMap() {
      this.tagFamilies.reverse().map(tagFamily => {
        this.orderMap.splice(2, 0, {
          name: tagFamily.tag,
          step: tagFamily.name
        });
      });
      this.tagFamilies.reverse();
    },
    setAndClose(options) {
      this.$store.dispatch("brain/global/setClearFilters").then(() => {
        this.setFilters(options);
      });
      this.show = false;
    },
    next(nextGuideItem) {
      if (nextGuideItem === "guideMeThrough") {
        this.throughAllSteps = true;
        if (this.$router.currentRoute.name !== "advanced") {
          this.$router.push({ name: "advanced" });
        }
      }

      const newIndex = this.orderMap.findIndex(
        item => item.name === nextGuideItem
      );
      if (newIndex >= 0) {
        this.active = this.orderMap[newIndex].name;
        this.activeStep = this.orderMap[newIndex].step
          ? this.orderMap[newIndex].step
          : null;
      }
    },
    prev(guideItem) {
      if (guideItem === "howCanIHelp") {
        this.active = "howCanIHelp";
      }
      const newIndex = this.orderMap.findIndex(item => item.name === guideItem);
      this.active = this.orderMap[newIndex].name;
      this.activeStep = this.orderMap[newIndex].step
        ? this.orderMap[newIndex].step
        : null;
    },
    to(guideItem) {
      let newIndex = this.orderMap.findIndex(item => item.name === guideItem);
      if (newIndex === -1) {
        this.loadTagFamiliesInOrderMap();
        newIndex = this.orderMap.findIndex(item => item.name === guideItem);
      }
      this.active = this.orderMap[newIndex].name;
      this.activeStep = this.orderMap[newIndex].step
        ? this.orderMap[newIndex].step
        : null;
    },
    stepClick(step) {
      const newIndex = this.orderMap.findIndex(item => item.step === step);
      this.active = this.orderMap[newIndex].name;
      this.activeStep = this.orderMap[newIndex].step;
    },
    setActiveStep(step) {
      this.activeStep = step;
    },
    setFilters(options) {
      EventBus.$emit(
        "brain/modules/handleFilterChange",
        {
          data: { name: options.tagFamily, values: options.checkedValues },
          addToHistory: true
        },
        { root: true }
      );
      EventBus.$emit(
        "brain/drivers/handleFilterChange",
        {
          data: { name: options.tagFamily, values: options.checkedValues },
          addToHistory: true
        },
        { root: true }
      );
    },
    activateMultiLength() {
      this.showLabels = false;
      this.activeStep = "Select family";
      this.active = "multiLength";
    },
    close() {
      this.show = false;
    },
    async onTunableWhiteGuide() {
      this.show = false;
      if (this.$route.name !== "advanced") {
        await this.$router.push({ name: "advanced" });

        /*
          The Advanced page fetches all filters from the backend and overrides already set filters.
          So, we wait until those filters are set and then override them with our own filters.
         */
        await Promise.all([
          new Promise(resolve => {
            const unwatch = this.$store.watch(
              (state, getters) => getters["brain/modules/rawFilters"],
              () => {
                unwatch();
                resolve();
              }
            );
          }),
          new Promise(resolve => {
            const unwatch = this.$store.watch(
              (state, getters) => getters["brain/drivers/rawFilters"],
              () => {
                unwatch();
                resolve();
              }
            );
          })
        ]);
      }
      await this.$store.dispatch("brain/global/setClearFilters");
      EventBus.$emit(
        "brain/modules/handleFilterChange",
        {
          data: { name: "special_spectrum", values: ["Tunable White"] },
          addToHistory: true
        },
        { root: true }
      );

      EventBus.$emit(
        "brain/drivers/handleFilterChange",
        {
          data: { name: "driverFeature", values: ["Tunable White (FlexTune)"] },
          addToHistory: true
        },
        { root: true }
      );

      EventBus.$emit("openModuleFiltersAccordion");
      EventBus.$emit("openModuleFilters", ["CCT"]);
    },
    onOldProductsGuide() {
      if (this.$route.name !== "advanced") {
        this.$router.push({ name: "advanced" });
      }

      this.$store.dispatch("brain/global/setClearFilters").then(() => {
        this.$store.commit("brain/global/setArchive", true, { root: true });

        // These handleFilterChanges are necessary to add the "Archived" value to the "Release state" filter.
        this.$store
          .dispatch("brain/modules/handleFilterChange", { addToHistory: false })
          .then(() => {
            EventBus.$emit(
              "brain/modules/handleFilterChange",
              {
                data: { name: "release_state", values: ["Archived"] }
              },
              { root: true }
            );
          });

        this.$store
          .dispatch("brain/drivers/handleFilterChange", { addToHistory: false })
          .then(() => {
            EventBus.$emit(
              "brain/drivers/handleFilterChange",
              {
                data: { name: "releaseState", values: ["Archived"] }
              },
              { root: true }
            );
          });
      });

      this.show = false;
    },
    onNextFamilyStep() {
      this.$refs.selectFamily.nextStep();
    },
    onPrevFamilyStep() {
      this.$refs.selectFamily.prevStep();
    },
    onShowFamilyResults() {
      this.$refs.selectFamily.showResults();
    }
  }
};
</script>
<style lang="scss">
.guide {
  .wizard-steps {
    margin-bottom: 60px;
  }

  .btn {
    cursor: pointer;
  }

  .guide-header {
    color: $blue;
    margin-bottom: 24px;
  }

  .custom-radio {
    margin-bottom: 2px;

    .custom-control-label {
      cursor: pointer;
    }
  }

  .btns-container {
    display: flex;
    justify-content: space-between;
    margin-top: 60px;
    margin-bottom: 20px;

    .btn {
      flex-grow: 2;
      display: block;
    }
  }

  .select-family {
    margin-bottom: 100px;
  }

  .footer-content {
    padding: 20px 4.6rem 35px 4.6rem;

    .footer-btns-container {
      display: flex;
      justify-content: space-between;

      .btn {
        flex-grow: 2;
        display: block;
      }
    }

    .show-results-wrapper {
      position: relative;
      top: 2rem;
      right: 0;

      .link-btn {
        position: absolute;
        right: 0;
        bottom: 0;
        text-decoration: underline;
        margin-top: 20px;
      }
    }
  }
}
</style>
